import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, CartesianGrid, AreaChart, Area, ComposedChart, Line } from 'recharts';
import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef, useId } from 'react';
import styled from 'styled-components';
import { useTranslation } from '../translations';
import { percentShares } from '../utils/percentShares';
import { API_BASE } from '../config';
import { PanelContainer as Container, Title, Subtitle, SecondaryButton } from '../ui/common';
import {
  SEZONY, okresKpi, zapytanieOkresu, rokSezonu, porownanieRokDoRoku, zapytanieOdcinka, zmianaRokDoRoku,
  opisZakresuDat, opisMiesiaca, sezonyWykresu, wskaznikiKlientow, wycinkiSprzetu,
} from '../utils/statystykiOkres';
// Allow dropdowns to overflow above KPI cards on this page
const LocalContainer = styled(Container)`
  overflow: visible;
`;

// Reuse inventory-style dropdown visuals (small listbox)
const DropdownWrapper = styled.div`
  position: relative;
  width: 100%;
  overflow: visible;
  z-index: var(--z-popover);
`;

const FilterDropdownButton = styled.button`
  width: 100%;
  padding: 10px 14px;
  min-height: 44px;
  border: 2px solid var(--input-border);
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 500;
  background: var(--input-bg);
  color: var(--text);
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  line-height: 1.2;
  transition: border-color .2s, box-shadow .2s, background .2s;
  gap: 8px;
  box-sizing: border-box;

  & > span:first-child {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: calc(100% - 28px);
  }

  &:hover { border-color: var(--primary); }
  &:focus-visible {
    outline: none;
    border-color: var(--primary);
    background: var(--card-bg);
    box-shadow: var(--focus-ring);
  }
  &[data-open="true"] {
    border-color: var(--primary);
    background: var(--card-bg);
    box-shadow: var(--focus-ring);
    svg { color: var(--primary); }
  }
  svg { flex-shrink: 0; transition: color .2s; color: var(--text-muted); }
`;

const FilterOptionsList = styled.ul`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  width: 100%;
  max-height: 220px;
  margin: 0;
  padding: 4px;
  list-style: none;
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: 12px;
  box-shadow: 0 8px 32px var(--shadow-color);
  overflow-y: auto;
  z-index: var(--z-popover);
  /* Fokus trzyma lista, a pozycję pod klawiaturą pokazuje podświetlenie (data-active). */
  outline: none;
`;

const FilterOptionItem = styled.li`
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--text);
  cursor: pointer;
  transition: background .15s, color .15s;

  .option-content { display:flex; align-items:center; gap:8px; }
  .option-content svg { color: var(--primary); flex-shrink:0; }
  .option-label { flex:1; }

  &:hover, &[data-active="true"] { background: var(--table-row-hover); }
  &[data-selected="true"] {
    background: rgba(59,130,246,0.12);
    color: var(--primary);
    font-weight: 600;
  }
`;

const DotIcon = () => (
  <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="4" cy="4" r="3" fill="currentColor" />
  </svg>
);

// Lista filtra okresu, obsługiwana także z klawiatury (przegląd końcowy, C8): strzałki, Home/End,
// Enter/Spacja wybiera, Escape zamyka, Tab zamyka i idzie dalej, a fokus wraca na przycisk. Ten
// sam model co components/ui/SelectDropdown.js, ale lista zostaje w drzewie strony (nie w
// portalu), bo filtr miesiąca przy sezonie pokazuje napis zastępczy, którego tamten nie ma.
function FilterDropdown({ value, onChange, options, placeholder, label, dataCy, waski = false }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapperRef = React.useRef(null);
  const triggerRef = React.useRef(null);
  const listRef = React.useRef(null);
  const listId = `${useId()}-lista`;

  useEffect(() => {
    if (!open) return;
    const poza = (e) => wrapperRef.current && !wrapperRef.current.contains(e.target);
    // Klik obok: fokus przenosi sama przeglądarka, wystarczy zamknąć listę.
    const onMouseDown = (e) => { if (poza(e)) setOpen(false); };
    // Przewinięcie strony: lista trzyma fokus, a po jej zniknięciu fokus spadłby na body,
    // więc wraca na przycisk (jak po Escape). Fokusu spoza filtra nie zabieramy.
    const onScroll = (e) => {
      if (!poza(e)) return;
      const fokusWFiltrze = wrapperRef.current.contains(document.activeElement);
      setOpen(false);
      if (fokusWFiltrze && triggerRef.current) triggerRef.current.focus({ preventScroll: true });
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('scroll', onScroll, true);
    const onWheel = (e) => {
      const t = e.target;
      if (t && t.tagName === 'INPUT' && t.type === 'number' && wrapperRef.current && wrapperRef.current.contains(t)) {
        e.preventDefault();
      }
    };
    document.addEventListener('wheel', onWheel, { passive: false, capture: true });
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('scroll', onScroll, true);
      document.removeEventListener('wheel', onWheel, { capture: true });
    };
  }, [open]);

  // Aktywna pozycja zawsze widoczna. Przewijamy WYŁĄCZNIE listę: scrollIntoView przewijałby też
  // stronę, a przewinięcie strony zamyka listę (nasłuch wyżej).
  useEffect(() => {
    const lista = listRef.current;
    if (!open || !lista) return;
    const el = lista.children[active];
    if (!el) return;
    const gora = el.offsetTop;
    const dol = gora + el.offsetHeight;
    if (gora < lista.scrollTop) lista.scrollTop = gora;
    else if (dol > lista.scrollTop + lista.clientHeight) lista.scrollTop = dol - lista.clientHeight;
  }, [open, active]);

  // Wartość spoza listy pokazuje placeholder, a nie pierwszą opcję: przy aktywnym sezonie filtr
  // miesiąca udawał wybrane „Cały rok”.
  const selectedIndex = options.findIndex(o => o.value === value);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : null;
  const shown = selected ? selected.label : placeholder;

  const openList = () => { setActive(Math.max(0, selectedIndex)); setOpen(true); };
  const close = (focusTrigger = true) => {
    setOpen(false);
    if (focusTrigger && triggerRef.current) triggerRef.current.focus({ preventScroll: true });
  };
  const choose = (i) => {
    const o = options[i];
    if (o) onChange(o.value);
    close();
  };

  // Fokus na listę w chwili, gdy pojawi się w dokumencie, bez przewijania strony.
  const attachList = useCallback((el) => {
    listRef.current = el;
    if (el) el.focus({ preventScroll: true });
  }, []);

  const onTriggerKey = (e) => {
    if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
      e.preventDefault();
      openList();
    }
  };

  const onListKey = (e) => {
    const last = options.length - 1;
    const ruchy = {
      ArrowDown: () => setActive((a) => Math.min(last, a + 1)),
      ArrowUp: () => setActive((a) => Math.max(0, a - 1)),
      Home: () => setActive(0),
      End: () => setActive(last),
      Enter: () => choose(active),
      ' ': () => choose(active),
      Escape: () => close(),
    };
    // Tab: fokus wraca na przycisk BEZ blokowania klawisza, więc przeglądarka przenosi go dalej
    // od przycisku, a lista nie zostaje otwarta za plecami użytkownika.
    if (e.key === 'Tab') { close(); return; }
    if (ruchy[e.key]) { e.preventDefault(); ruchy[e.key](); }
  };

  // Fokus wyszedł poza filtr inną drogą niż Tab (np. czytnik ekranu): lista się zamyka. Bez
  // `relatedTarget` (klik w miejsce, które fokusu nie bierze) decyduje nasłuch `mousedown`.
  const onBlurWrapper = (e) => {
    const dokad = e.relatedTarget;
    if (open && dokad && wrapperRef.current && !wrapperRef.current.contains(dokad)) setOpen(false);
  };

  return (
    <FiltrPole data-waski={waski}>
      <DropdownWrapper ref={wrapperRef} onBlur={onBlurWrapper}>
        <FilterDropdownButton
          ref={triggerRef}
          type="button"
          data-cy={dataCy}
          data-open={open}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-label={label && label !== shown ? `${label}: ${shown}` : undefined}
          onClick={() => (open ? close() : openList())}
          onKeyDown={onTriggerKey}
        >
          <span>{shown}</span>
          {open ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M18 15l-6-6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          )}
        </FilterDropdownButton>

        {open && (
          <FilterOptionsList
            ref={attachList}
            id={listId}
            role="listbox"
            tabIndex={-1}
            aria-label={label}
            aria-activedescendant={`${listId}-${active}`}
            onKeyDown={onListKey}
          >
            {options.map((o, i) => {
              const sel = o.value === value;
              return (
                <FilterOptionItem
                  key={o.value}
                  id={`${listId}-${i}`}
                  role="option"
                  aria-selected={sel}
                  data-selected={sel}
                  data-active={i === active}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => choose(i)}
                >
                  <span className="option-content">
                    {sel && <DotIcon />}
                    <span className="option-label">{o.label}</span>
                  </span>
                </FilterOptionItem>
              );
            })}
          </FilterOptionsList>
        )}
      </DropdownWrapper>
    </FiltrPole>
  );
}
const Header = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;



const ArrowButton = styled.button`
  background: var(--card-bg);
  color: var(--text);
  border: 1px solid var(--input-border);
  border-radius: 10px;
  padding: 8px 12px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 44px;
  font-weight: 700;
  &:hover:not(:disabled) { background: var(--hover-bg); transform: translateY(-1px); }
  &:disabled { opacity: .5; cursor: not-allowed; }
`;

const YearText = styled.span`
  align-self: center;
  padding: 6px 12px;
  border-radius: 10px;
  background: var(--panel-bg);
  border: 1px solid var(--input-border);
  color: var(--text);
  font-weight: 600;
`;


// FilterSelect imported from ../ui/common

const ControlsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: 14px;
  padding: 10px 16px;
  margin-bottom: 14px;
`;

const MetricPills = styled.div`
  display: flex;
  gap: 3px;
  background: var(--panel-bg);
  border-radius: 9px;
  padding: 3px;
  max-width: 100%;

  /* Na telefonie dwa przyciski po co najmniej 44px (reguła dotyku) i bez łamania tekstu były
     szersze od karty i wystawały poza jej ramkę (przegląd 28.09.2026). */
  @media (max-width: 480px) {
    width: 100%;
    & > button { flex: 1 1 0; min-width: 0; white-space: normal; }
  }
`;

// Tło zaznaczonej pigułki miary i plakietki okresu. Sam --primary nie trzyma kontrastu 4,5:1 dla
// drobnego tekstu (biały na #3b82f6 to 3,7:1, a w ciemnym motywie biały na #4aa3ff 2,6:1), więc
// kolor marki jest przesunięty w stronę --text: w jasnym motywie ciemnieje, w ciemnym jaśnieje,
// a tekst bierze --primary-contrast (biały w jasnym, granatowy w ciemnym). Zmierzone: 5,2:1 i 8,6:1.
const TLO_ZAZNACZENIA = 'color-mix(in srgb, var(--primary) 75%, var(--text))';

const PillButton = styled.button`
  background: ${p => p.$active ? TLO_ZAZNACZENIA : 'transparent'};
  color: ${p => p.$active ? 'var(--primary-contrast)' : 'var(--text-muted)'};
  border: none;
  padding: 5px 15px;
  font-size: 0.87rem;
  font-weight: 600;
  border-radius: 7px;
  cursor: pointer;
  transition: all .15s;
  box-shadow: ${p => p.$active ? '0 2px 8px rgba(59,130,246,0.38)' : 'none'};
  white-space: nowrap;
  /* Najechanie zmienia kolor tylko nieaktywnej: --text na tle zaznaczenia to 3:1. */
  &:hover { color: ${p => p.$active ? 'var(--primary-contrast)' : 'var(--text)'}; }
`;

const YearNav = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ControlsDivider = styled.div`
  width: 1px;
  height: 22px;
  background: var(--input-border);
  flex-shrink: 0;

  /* Po zawinięciu paska kreska zostawała sama na początku wiersza. */
  @media (max-width: 768px) { display: none; }
`;

const ControlsHint = styled.span`
  margin-left: auto;
  font-size: 0.83rem;
  color: var(--text-muted);
  white-space: nowrap;
  min-width: 0;

  @media (max-width: 768px) {
    margin-left: 0;
    white-space: normal;
    font-size: 0.78rem;
    width: 100%;
    word-wrap: break-word;

    & > span {
      display: block;
      margin-left: 0 !important;
      margin-top: 2px;
    }
  }
`;

const MainChartCard = styled.div`
  background: var(--card-bg);
  border-radius: 16px;
  border: 1px solid var(--input-border);
  box-shadow: 0 4px 20px var(--shadow-color);
  padding: 18px 18px 10px 18px;
  margin-bottom: 14px;
`;

const SmallChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 14px;
  margin-bottom: 14px;
  @media (max-width: 960px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ChartRowHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 6px;
`;

/* kept for backwards compat */

const KPIContainer = styled.div`
  background: var(--card-bg);
  border-radius: 16px;
  border: 1px solid var(--input-border);
  box-shadow: 0 8px 24px var(--shadow-color);
  padding: 24px;
  margin-bottom: 24px;
`;

const PeriodLabel = styled.div`
  font-size: 0.95rem;
  color: var(--text-muted);
  margin-top: 8px;
  margin-bottom: 12px;
`;

const KPIBadgesRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 18px;
`;

const KPIBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  color: ${p => p.$tone === 'primary' ? 'var(--primary-contrast)' : 'var(--text)'};
  background: ${p => p.$tone === 'primary'
    ? TLO_ZAZNACZENIA
    : 'var(--panel-bg)'};
  border: 1px solid ${p => p.$tone === 'primary' ? 'transparent' : 'var(--input-border)'};
`;

const KPIHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const KPIFiltersRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;

  /* „Wyczyść” obok sezonu, nie w osobnym rzędzie (SecondaryButton ma tu szerokość 100%). */
  @media (max-width: 768px) {
    & > button { width: auto; flex: 0 0 auto; }
  }
`;

// Pole filtra okresu. Na wąskim ekranie filtry idą w dwa rzędy (rok z miesiącem, sezon z
// „Wyczyść”) zamiast czterech: pod nimi zostaje miejsce na kartę przewodnika po stronie, która na
// telefonie zasłaniała przycisk „Wyczyść”, o którym mówi (przegląd odbiorczy, 10.2026).
const FiltrPole = styled.div`
  min-width: 160px;

  @media (max-width: 768px) {
    min-width: 0;
    flex: 1 1 140px;
    &[data-waski='true'] { flex: 0 0 104px; }
  }
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 22px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const SummaryCard = styled.div`
  position: relative;
  overflow: hidden;
  background: linear-gradient(180deg, color-mix(in srgb, var(--panel-bg) 88%, ${p => p.$accent || 'var(--primary)'} 12%), var(--panel-bg));
  border: 1px solid color-mix(in srgb, ${p => p.$accent || 'var(--primary)'} 35%, transparent);
  border-radius: 16px;
  padding: 18px;
  box-shadow: var(--shadow-md);

  &::after {
    content: '';
    position: absolute;
    right: -24px;
    top: -24px;
    width: 92px;
    height: 92px;
    border-radius: 50%;
    background: color-mix(in srgb, ${p => p.$accent || 'var(--primary)'} 18%, transparent);
  }
`;

const SummaryLabel = styled.div`
  position: relative;
  z-index: 1;
  font-size: 0.82rem;
  color: var(--text-muted);
  margin-bottom: 10px;
  font-weight: 600;
`;

const SummaryValue = styled.div`
  position: relative;
  z-index: 1;
  font-size: 1.8rem;
  line-height: 1.1;
  font-weight: 800;
  color: var(--text);
`;

const SummaryMeta = styled.div`
  position: relative;
  z-index: 1;
  margin-top: 10px;
  font-size: 0.84rem;
  color: var(--text-muted);
`;

/**
 * Nota pod kafelkiem. Widoczna, a nie w dymku `title`: ta sama informacja w dymku jest
 * niedostępna dotykiem, a Statystyki ogląda się także na tablecie.
 */
const SummaryHint = styled.div`
  position: relative;
  z-index: 1;
  margin-top: 6px;
  font-size: 0.76rem;
  line-height: 1.35;
  color: var(--text-muted);
`;






/* nowy tytuł sekcji KPI */
const SectionTitle = styled.h2`
  margin: 0 0 18px 0;
  color: var(--text);
  font-size: 1.25rem;
  font-weight: 700;
`;

/* zmieniony layout: 2 kolumny kategorii (2x2) */
const KPIGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr)); /* 2 kolumny - po 2 sekcje w wierszu */
  gap: 28px;
  align-items: start;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

/* Sekcja - pojedyncza kategoria z nagłówkiem */
const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
`;

/* brakujący nagłówek sekcji */
const SectionHeader = styled.h3`
  margin: 0;
  padding: 8px 12px;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text);
  background: rgba(255,255,255,0.02);
  border-radius: 8px;
  align-self: flex-start;
`;

/* Grid wewnątrz sekcji: 2 kolumny kart KPI */
const SectionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  align-items: stretch;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

/* Stała wysokość kart i wyrównanie zawartości */
const KPICard = styled.div`
  background: var(--panel-bg);
  border-radius: 8px;
  padding: 18px;
  text-align: center;
  border: 1px solid rgba(255,255,255,0.03);
  box-shadow: var(--shadow-md);

  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  height: 140px;
  min-height: 140px;
  min-width: 0;
  box-sizing: border-box;

  p {
    color: var(--text-muted);
    margin: 0;
    font-size: 0.95rem;
    white-space: normal;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-word;
  }

  span {
    color: var(--text);
    font-size: 1.6rem;
    font-weight: 700;
    display: block;
  }

  small {
    color: var(--text-muted);
    font-size: 0.8rem;
    line-height: 1.35;
  }
`;


const ChartCard = styled.div`
  background: var(--card-bg);
  border-radius: 14px;
  border: 1px solid var(--input-border);
  box-shadow: 0 3px 14px var(--shadow-color);
  padding: 14px 14px 8px 14px;
`;

const ChartTitle = styled.h3`
  margin: 0 0 2px 0;
  font-size: 0.92rem;
  font-weight: 700;
  color: var(--text);
`;

const ChartSubtitle = styled.p`
  margin: 0 0 10px 0;
  font-size: 0.78rem;
  color: var(--text-muted);
`;

// Legenda jako zwykła lista POD wykresem, nie <Legend> recharts: tamta leżała w warstwie
// absolutnej i przy wąskiej karcie zasłaniała etykiety procentów pączków, układała pozycje
// alfabetycznie (zima na końcu) i dawała ikonom angielskie nazwy „… legend icon” (przegląd
// odbiorczy, 10.2026). Tu kolejność to kolejność danych, a znak koloru jest ukryty przed czytnikiem.
const LegendaLista = styled.ul`
  list-style: none;
  margin: 6px 0 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 4px 14px;
  font-size: 0.78rem;
  line-height: 1.4;
  color: var(--text);
`;

const LegendaPozycja = styled.li`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const LegendaZnak = styled.span`
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
`;

function LegendaWykresu({ pozycje, dataCy }) {
  return (
    <LegendaLista data-cy={dataCy}>
      {pozycje.map((p) => (
        <LegendaPozycja key={p.nazwa}>
          <LegendaZnak aria-hidden="true" style={{ background: p.kolor }} />
          <span>{p.nazwa}</span>
        </LegendaPozycja>
      ))}
    </LegendaLista>
  );
}

// Wysokość pierścieni i napisu wczytywania w ich miejscu (ta sama, więc karta nie skacze).
const WYSOKOSC_PACZKA = 168;

// Wykres, którego dane jeszcze nie należą do pokazywanego roku: zamiast słupków poprzedniego roku
// pod nowym podpisem sam napis, więc nie ma też czego kliknąć (klik w słupek przestawiłby kafelki
// na miesiąc wzięty z cudzych danych).
function WykresWczytywany({ wysokosc, tekst }) {
  return (
    <div
      data-cy="stats-chart-loading"
      aria-busy="true"
      style={{ height: wysokosc, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}
    >
      {tekst}
    </div>
  );
}

// Legenda pierścieni („Status sprzętu”, „Sezonowość”) jako tabelka pod wykresem. Wcześniej
// procenty były etykietami NA ZEWNĄTRZ pierścienia, a w wąskiej karcie (3 wykresy w rzędzie)
// nie mieściły się w SVG i były ucinane. W tabelce liczba i procent zawsze się mieszczą.
const DonutLegend = styled.ul`
  list-style: none;
  margin: 4px 0 6px;
  padding: 0;
  display: grid;
  gap: 6px;
  font-size: 0.8rem;
`;

const DonutLegendRow = styled.li`
  display: grid;
  grid-template-columns: 10px minmax(0, 1fr) auto 3.2em;
  align-items: center;
  gap: 8px;
  color: var(--text);
  font-variant-numeric: tabular-nums;

  & > span:nth-child(2) { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  & > span:nth-child(3) { color: var(--text-muted); }
  & > strong { text-align: right; font-weight: 700; }
`;

// Wiersz legendy jako przycisk (Sezonowość: klik filtruje statystyki po porze roku).
const DonutLegendButton = styled.button`
  width: calc(100% + 12px);
  margin: -3px -6px;
  padding: 3px 6px;
  display: grid;
  grid-template-columns: 10px minmax(0, 1fr) auto 3.2em;
  align-items: center;
  gap: 8px;
  border: none;
  border-radius: 6px;
  background: ${(p) => (p.$active ? 'var(--table-row-hover)' : 'transparent')};
  color: var(--text);
  font: inherit;
  font-variant-numeric: tabular-nums;
  text-align: left;
  cursor: pointer;

  &:hover { background: var(--table-row-hover); }
  &:focus-visible { outline: 2px solid var(--primary); outline-offset: 1px; }

  & > span:nth-child(2) { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  & > span:nth-child(3) { color: var(--text-muted); }
  & > strong { text-align: right; font-weight: 700; }
`;

const DonutLegendDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  opacity: 0.8; /* jak wycinki pierścienia, które mają gradient krycia 0.9 → 0.65 */
`;

// Kolory pór roku: powszechnie rozpoznawalna konwencja (zima niebieska, lato żółte),
// celowo poza tokenami tonu, jak medale w RankBadge.
const KOLORY_SEZONOW = { winter: '#3b82f6', spring: '#22c55e', summer: '#f2c94c', autumn: '#f97316' };

const LOKALE = { pl: 'pl-PL', en: 'en-GB', de: 'de-DE' };

// Dymki recharts domyślnie rozdzielają nazwę i wartość napisem „ : ”, inaczej niż reszta ekranu.
const SEPARATOR_DYMKA = ': ';

// Wykres „Sezonowość {rok}” w metrykach zaawansowanych: dymek i legenda w kolejności rysowania serii
// (słupki rezerwacji, potem linia wartości). Recharts domyślnie układa obie alfabetycznie po
// PRZETŁUMACZONEJ nazwie (dymek po `name`, legenda po `value`), więc kolejność zależała od języka
// i odwracała się przy zmianie napisu (np. „Wypożyczenia” i „Wartość”). Klucz danych się nie tłumaczy.
const SERIE_SEZONOWOSCI = ['rentals', 'revenue'];
const wKolejnosciSeriiSezonowosci = (pozycja) => SERIE_SEZONOWOSCI.indexOf(pozycja?.dataKey);

// Wycinki pączka „Sprzęt dziś” (klucze z `wycinkiSprzetu`). Pole nazywa się `labelKey`, bo
// tylko tak audyt tłumaczeń widzi klucz trzymany w danych.
const WYCINKI_SPRZETU = {
  inUse: { labelKey: 'statistics.chart.inUseToday', kolor: 'var(--primary)' },
  available: { labelKey: 'statistics.chart.availableToday', kolor: 'var(--success)' },
  outOfService: { labelKey: 'statistics.chart.outOfServiceToday', kolor: 'var(--warning)' },
};

// Pobranie JSON-a z przerwaniem: odpowiedź filtra, który już zmieniono, NIE może nadpisać
// nowszej (dawniej wybór sierpnia i zaraz września kończył się napisem „Wrzesień 2026”
// z kwotą sierpnia, przegląd 28.09.2026).
function pobierz(adres, sygnal) {
  return fetch(adres, { credentials: 'include', signal: sygnal }).then((r) => {
    if (!r.ok) {
      const blad = new Error(`HTTP ${r.status}`);
      blad.status = r.status;
      throw blad;
    }
    return r.json();
  });
}
const przerwane = (e) => e && e.name === 'AbortError';

// Przychód i rezerwacje odcinka porównania rok do roku: `{ revenue, rentals }`. Lekka trasa liczy
// same te dwie liczby (pełne `/api/kpis-extended` to około 25 zapytań do bazy na dwa pola).
// Celowo BEZ zapasu do `/api/kpis-extended` przy 404: serwer sprzed tej trasy nie zna `from`/`to`
// i bez `year` liczy bieżący miesiąc, więc oba odcinki dostawały te same liczby, a ekran podawał
// zmyślony procent (przegląd końcowy, C3). Każdy błąd, także 404, daje brak porównania.
function sumyOdcinka(odcinek, sygnal) {
  return pobierz(`${API_BASE}/api/period-totals?${zapytanieOdcinka(odcinek)}`, sygnal);
}

// Wartości miesięcy z odpowiedzi `/api/monthly-rentals` jako 12 liczb (styczeń..grudzień).
function miesiaceZOdpowiedzi(dane, pole) {
  const wynik = new Array(12).fill(0);
  (Array.isArray(dane) ? dane : []).forEach((m, i) => {
    const nr = Number(m?.month_num) || i + 1;
    if (nr >= 1 && nr <= 12) wynik[nr - 1] = Number(m?.[pole] || 0);
  });
  return wynik;
}

export default function Statistics() {
  const { t, currentLanguage } = useTranslation();
  const teraz = new Date();
  const [chartYear, setChartYear] = useState(teraz.getFullYear());
  const [kpiYear, setKpiYear] = useState(String(teraz.getFullYear()));
  // Pusty miesiąc = CAŁY ROK. Dawniej znaczył „bieżący miesiąc”, więc strzałka roku, która go
  // czyściła, przestawiała kafelki na ten sam miesiąc innego roku, a wykres na cały rok.
  const [kpiMonth, setKpiMonth] = useState(String(teraz.getMonth() + 1));
  const [kpiSeason, setKpiSeason] = useState('');
  const [metric, setMetric] = useState('count'); // 'count' | 'value'

  // Odpowiedzi trzymają KLUCZ tego, o co pytano (okres kafelków, rok wykresu), a ekran traktuje
  // dane z innym kluczem jak wczytywanie. Czyszczenie stanu w efekcie przychodziło za późno: efekt
  // biegnie po pierwszym rysowaniu, więc przez klatkę nad liczbami poprzedniego okresu stała już
  // etykieta nowego (przegląd odbiorczy, 10.2026).
  // /api/kpis-extended: `klucz` = zapytanie okresu, `blad` = odpowiedź z błędem.
  const [kafelki, setKafelki] = useState({ klucz: null, dane: null, blad: false });
  // /api/advanced-metrics, ten sam klucz co kafelki.
  const [zaawansowane, setZaawansowane] = useState({ klucz: null, dane: null });
  // Wysokość sekcji zaawansowanej z liczbami, mierzona po każdym rysowaniu: na czas wczytywania
  // sekcja zostaje z tą wysokością, bo jej zniknięcie skracało stronę o ponad 1000 px i przeglądarka
  // przycinała przewinięcie, więc filtr kafelków uciekał spod kursora. 0 = sekcji nie było.
  const wysokoscZaawansowanychRef = useRef(0);
  const sekcjaZaawansowanaRef = useRef(null);
  // Porównanie rok do roku: `plan` to plan, dla którego przyszły odpowiedzi (inny plan = jeszcze
  // się wczytuje), `biezacy` i `poprzedni` to `{ revenue, rentals }` odcinków (`sumyOdcinka`).
  // `biezacy` undefined = odcinek równy okresowi kafelków, więc wartość bierzemy z `kpi`.
  const [rokDoRoku, setRokDoRoku] = useState({ plan: null, biezacy: undefined, poprzedni: null });
  // Wykresy: `rok` = rok wykresu, dla którego przyszły odpowiedzi.
  const [monthly, setMonthly] = useState({ rok: null, biezacy: null, poprzedni: null });

  const lokal = LOKALE[currentLanguage] || 'pl-PL';
  const currencyFmt = useMemo(() => new Intl.NumberFormat(lokal, { style: 'currency', currency: 'PLN', maximumFractionDigits: 0 }), [lokal]);
  const currency2Fmt = useMemo(() => new Intl.NumberFormat(lokal, { style: 'currency', currency: 'PLN', minimumFractionDigits: 2, maximumFractionDigits: 2 }), [lokal]);
  const numberFmt = useMemo(() => new Intl.NumberFormat(lokal), [lokal]);
  const decimalFmt = useMemo(() => new Intl.NumberFormat(lokal, { minimumFractionDigits: 1, maximumFractionDigits: 1 }), [lokal]);
  const decimal2Fmt = useMemo(() => new Intl.NumberFormat(lokal, { minimumFractionDigits: 2, maximumFractionDigits: 2 }), [lokal]);

  const localizedMonths = useMemo(() => {
    const nazwy = t('calendar.months');
    return Array.isArray(nazwy) && nazwy.length === 12 ? nazwy : Array.from({ length: 12 }, (_, i) => String(i + 1));
  }, [t]);

  const nazwaSezonu = useCallback((value) => t(`statistics.season.${value}`), [t]);

  const okres = useMemo(
    () => okresKpi({ rok: Number(kpiYear) || new Date().getFullYear(), miesiac: kpiSeason ? '' : kpiMonth, sezon: kpiSeason }),
    [kpiYear, kpiMonth, kpiSeason]
  );
  const etykietaOkresu = useMemo(() => {
    if (okres.rodzaj === 'sezon') return `${nazwaSezonu(okres.sezon)} ${rokSezonu(okres.sezon, okres.rok)}`;
    if (okres.rodzaj === 'miesiac') return `${localizedMonths[okres.miesiac - 1]} ${okres.rok}`;
    return `${t('statistics.wholeYear')} ${okres.rok}`;
  }, [okres, nazwaSezonu, localizedMonths, t]);
  const zakresOkresu = useMemo(() => {
    const d = (s) => new Date(`${s}T00:00:00`).toLocaleDateString(lokal);
    const ostatni = new Date(`${okres.doWylacznie}T00:00:00`);
    ostatni.setDate(ostatni.getDate() - 1);
    return `${d(okres.od)} - ${ostatni.toLocaleDateString(lokal)}`;
  }, [okres, lokal]);
  // Plan porównania liczony przy zmianie okresu; nowy obiekt planu unieważnia odpowiedzi starego.
  const planPorownania = useMemo(() => porownanieRokDoRoku(okres, new Date()), [okres]);
  const kluczOkresu = useMemo(() => zapytanieOkresu(okres), [okres]);

  // Dane z kluczem bieżącego okresu albo nic (= wczytywanie). Pączek „Sprzęt dziś” nie zależy od
  // okresu, więc bierze ostatnią odpowiedź bez względu na klucz i nie miga przy zmianie filtra.
  const kafelkiGotowe = kafelki.klucz === kluczOkresu;
  const kpi = kafelkiGotowe ? kafelki.dane : null;
  const isLoading = !kafelkiGotowe;
  const loadError = kafelkiGotowe && kafelki.blad;
  const sprzetDzis = kafelki.dane ? kafelki.dane.equipment : null;
  const zaawansowaneGotowe = zaawansowane.klucz === kluczOkresu;
  const advancedMetrics = zaawansowaneGotowe ? zaawansowane.dane : null;
  const wykresGotowy = monthly.rok === chartYear;

  // Pomiar po każdym rysowaniu i po zmianie rozmiaru okna, ale tylko sekcji z liczbami: w trakcie
  // wczytywania sekcja ma narzuconą dawną wysokość i pomiar niczego by nie dodał.
  const zmierzZaawansowane = useCallback(() => {
    const sekcja = sekcjaZaawansowanaRef.current;
    if (!sekcja) { wysokoscZaawansowanychRef.current = 0; return; }
    if (sekcja.getAttribute('aria-busy') === 'true') return;
    wysokoscZaawansowanychRef.current = Math.round(sekcja.getBoundingClientRect().height);
  }, []);
  useLayoutEffect(() => { zmierzZaawansowane(); });
  useEffect(() => {
    window.addEventListener('resize', zmierzZaawansowane);
    return () => window.removeEventListener('resize', zmierzZaawansowane);
  }, [zmierzZaawansowane]);

  const applyKpiMonthFilter = useCallback((year, month) => {
    setKpiYear(String(year)); setKpiMonth(String(month)); setKpiSeason('');
  }, []);
  const applyKpiSeasonFilter = useCallback((year, seasonValue) => {
    setKpiYear(String(year)); setKpiMonth(''); setKpiSeason(String(seasonValue || ''));
  }, []);
  // Razem z rokiem wykresu: po „Wyczyść” kafelki wracały na bieżący miesiąc, a wykres i pączek
  // zostawały przy roku ze strzałek.
  const clearKpiFilters = useCallback(() => {
    const d = new Date();
    setChartYear(d.getFullYear());
    setKpiYear(String(d.getFullYear())); setKpiMonth(String(d.getMonth() + 1)); setKpiSeason('');
  }, []);
  // Strzałki roku przestawiają wykres i kafelki na CAŁY wybrany rok, żeby oba mówiły o tym samym.
  const zmienRok = (krok) => {
    const minY = 2000;
    const maxY = new Date().getFullYear() + 1;
    const ny = Math.min(maxY, Math.max(minY, chartYear + krok));
    setChartYear(ny); setKpiYear(String(ny)); setKpiMonth(''); setKpiSeason('');
  };

  // --- Kafelki: JEDNO źródło (`/api/kpis-extended`) ---
  // Efekt niczego nie czyści: dane starego okresu mają inny klucz, więc już przy pierwszym
  // rysowaniu po zmianie filtra ekran pokazuje wczytywanie. Przerwanie (AbortController)
  // pilnuje, żeby spóźniona odpowiedź starego filtra nie zapisała się po nowej.
  useEffect(() => {
    const ac = new AbortController();
    const klucz = kluczOkresu;
    const zapisz = (ustaw, wartosc) => { if (!ac.signal.aborted) ustaw(wartosc); };
    pobierz(`${API_BASE}/api/kpis-extended?${klucz}`, ac.signal)
      .then((dane) => zapisz(setKafelki, { klucz, dane, blad: false }))
      .catch((e) => { if (!przerwane(e)) zapisz(setKafelki, { klucz, dane: null, blad: true }); });
    pobierz(`${API_BASE}/api/advanced-metrics?${klucz}`, ac.signal)
      .then((dane) => zapisz(setZaawansowane, { klucz, dane: dane && dane.utilization ? dane : null }))
      .catch((e) => { if (!przerwane(e)) zapisz(setZaawansowane, { klucz, dane: null }); });
    // Porównanie rok do roku z tym samym przerwaniem co kafelki. Błąd odcinka = brak porównania.
    const plan = planPorownania;
    if (plan.poprzedni) {
      const wartosciOdcinka = (odcinek) => sumyOdcinka(odcinek, ac.signal)
        .catch((e) => { if (przerwane(e)) throw e; return null; });
      // Okres zakończony to ten sam odcinek co kafelki, więc bez drugiego zapytania o niego.
      const osobno = plan.biezacy.od !== okres.od || plan.biezacy.doWylacznie !== okres.doWylacznie;
      Promise.all([osobno ? wartosciOdcinka(plan.biezacy) : undefined, wartosciOdcinka(plan.poprzedni)])
        .then(([biezacy, poprzedni]) => zapisz(setRokDoRoku, { plan, biezacy, poprzedni }))
        .catch(() => {}); // tu trafia wyłącznie przerwanie: błędy pobrania łapie wartosciOdcinka
    }
    return () => ac.abort();
  }, [okres, kluczOkresu, planPorownania]);

  // --- Wykresy: rok wykresu i poprzedni (grudzień poprzedniego roku należy do zimy) ---
  // Odpowiedź niesie swój rok; do jej przyjścia wykresy nowego roku pokazują wczytywanie.
  useEffect(() => {
    const ac = new AbortController();
    const rok = chartYear;
    const zapisz = (wartosc) => { if (!ac.signal.aborted) setMonthly(wartosc); };
    Promise.all([
      pobierz(`${API_BASE}/api/monthly-rentals?year=${rok}`, ac.signal),
      pobierz(`${API_BASE}/api/monthly-rentals?year=${rok - 1}`, ac.signal).catch((e) => { if (przerwane(e)) throw e; return null; }),
    ])
      .then(([biezacy, poprzedni]) => zapisz({ rok, biezacy, poprzedni }))
      .catch((e) => { if (!przerwane(e)) zapisz({ rok, biezacy: null, poprzedni: null }); });
    return () => ac.abort();
  }, [chartYear]);

  const pole = metric === 'value' ? 'rental_value' : 'rental_count';
  const monthlyData = useMemo(() => {
    const liczby = miesiaceZOdpowiedzi(monthly.biezacy, 'rental_count');
    const wartosci = miesiaceZOdpowiedzi(monthly.biezacy, 'rental_value');
    return localizedMonths.map((nazwa, i) => ({
      month: nazwa, monthIndex: i + 1, rental_count: liczby[i], rental_value: wartosci[i],
    }));
  }, [monthly.biezacy, localizedMonths]);

  // Sezony roku wykresu; zima = grudzień poprzedniego roku + styczeń i luty (decyzja Piotrka).
  const seasonalData = useMemo(() => sezonyWykresu(
    miesiaceZOdpowiedzi(monthly.biezacy, pole),
    miesiaceZOdpowiedzi(monthly.poprzedni, pole),
  ).map((s) => ({
    key: s.value,
    name: s.value === 'winter' ? `${nazwaSezonu(s.value)} ${rokSezonu(s.value, chartYear)}` : nazwaSezonu(s.value),
    value: s.suma,
    percent: s.procent,
    color: KOLORY_SEZONOW[s.value],
  })), [monthly, pole, chartYear, nazwaSezonu]);
  // Podświetlenie tylko dla tego samego sezonu TEGO SAMEGO roku: po zmianie roku w filtrze
  // pączek podświetlał „Zima 2023/24” obok kafelków „Zima 2025/26”.
  const sezonWykresuWybrany = (klucz) => okres.rodzaj === 'sezon' && okres.sezon === klucz && okres.rok === chartYear;

  const trendData = useMemo(() => monthlyData.map((m) => ({
    month: m.month.slice(0, 3), monthIndex: m.monthIndex, count: m.rental_count, value: m.rental_value,
  })), [monthlyData]);

  // --- Wskaźniki okresu ---
  const p = kpi?.period || {};
  const sprzet = kpi?.equipment || {};
  const klienci = wskaznikiKlientow(kpi?.period, kpi?.customers);
  const aktywni = klienci.aktywni;
  const przychodOkresu = Number(p.revenue || 0);
  const rezerwacjeOkresu = Number(p.rentals || 0);
  const liczbaSztuk = Number(sprzet.totalEquipment || 0);
  const dniDoDyspozycji = liczbaSztuk * Number(p.days || 0);
  const liczbaSztukDzis = Number(sprzetDzis?.totalEquipment || 0);

  // Porównanie rok do roku (decyzja Piotrka, 04.10.2026). Dopóki odpowiedzi nie należą do planu
  // BIEŻĄCEGO okresu, procentu nie ma: inaczej po zmianie filtra stał tu procent poprzedniego.
  const porownanie = useMemo(() => {
    if (planPorownania.stan === 'przyszly') return { stan: 'przyszly', przychod: null, rezerwacje: null };
    if (rokDoRoku.plan !== planPorownania) return { stan: 'ladowanie', przychod: null, rezerwacje: null };
    // `kpi` to już dane TEGO okresu albo nic, więc odcinek równy okresowi kafelków nie weźmie
    // liczb poprzedniego (podpis porównania i tak stoi tylko przy kafelkach z danymi).
    const biezacy = rokDoRoku.biezacy === undefined ? kpi?.period : rokDoRoku.biezacy;
    const poprzedni = rokDoRoku.poprzedni;
    return {
      stan: 'gotowe',
      przychod: biezacy && poprzedni ? zmianaRokDoRoku(biezacy.revenue, poprzedni.revenue) : null,
      rezerwacje: biezacy && poprzedni ? zmianaRokDoRoku(biezacy.rentals, poprzedni.rentals) : null,
    };
  }, [planPorownania, rokDoRoku, kpi]);

  // Z czym porównujemy, słowami: okres w toku datami odcinka (procent nie dotyczy całego kafelka),
  // zakończony nazwą okresu rok wcześniej. Każdy sezon osobnym wywołaniem `t`, żeby audyt
  // tłumaczeń widział klucze.
  const odcinekPorownania = useMemo(() => {
    const poprz = planPorownania.poprzedni;
    if (!poprz) return '';
    if (planPorownania.stan === 'w_toku') return opisZakresuDat(poprz.od, poprz.doWylacznie, lokal);
    if (okres.rodzaj === 'miesiac') return opisMiesiaca(poprz.od, lokal);
    const rokPoprz = okres.rok - 1;
    if (okres.rodzaj === 'rok') return t('statistics.change.year', { year: rokPoprz });
    const label = rokSezonu(okres.sezon, rokPoprz);
    if (okres.sezon === 'winter') return t('statistics.change.season.winter', { label });
    if (okres.sezon === 'spring') return t('statistics.change.season.spring', { label });
    if (okres.sezon === 'summer') return t('statistics.change.season.summer', { label });
    return t('statistics.change.season.autumn', { label });
  }, [planPorownania, okres, lokal, t]);

  const procentZmiany = (procent) => `${procent > 0 ? '+' : ''}${decimalFmt.format(procent)}%`;
  // `zProcentem` = dopisek pod kafelkiem („+18,0% wobec…”); bez niego sam odcinek do karty „Zmiana”.
  const formatZmiany = (procent, zProcentem = true) => {
    if (porownanie.stan === 'przyszly') return t('statistics.change.notStarted');
    if (porownanie.stan === 'ladowanie') return '\u00a0'; // miejsce na podpis, bez skoku układu
    if (procent == null) return t('statistics.change.noComparison');
    // Okres w toku dostaje dopisek, że trwa (prośba Piotrka): porównujemy tylko dni, które minęły.
    if (planPorownania.stan === 'w_toku') {
      return zProcentem
        ? t('statistics.change.vsPeriodOngoing', { value: procentZmiany(procent), period: odcinekPorownania })
        : t('statistics.change.comparedWithOngoing', { period: odcinekPorownania });
    }
    return zProcentem
      ? t('statistics.change.vsPeriod', { value: procentZmiany(procent), period: odcinekPorownania })
      : t('statistics.change.comparedWith', { period: odcinekPorownania });
  };

  const equipmentDonutData = useMemo(() => {
    const brak = [{ name: t('statistics.chart.noData'), value: 1, color: 'var(--input-border)' }];
    if (!liczbaSztukDzis) return brak;
    const out = wycinkiSprzetu(sprzetDzis).map((w) => ({
      name: t(WYCINKI_SPRZETU[w.klucz].labelKey), value: w.value, color: WYCINKI_SPRZETU[w.klucz].kolor,
    }));
    return out.length ? out : brak;
  }, [sprzetDzis, liczbaSztukDzis, t]);

  // Procenty w legendach obu pierścieni: liczby całkowite sumujące się do 100.
  const equipmentShares = useMemo(() => percentShares(equipmentDonutData.map((e) => e.value)), [equipmentDonutData]);
  const seasonalShares = useMemo(() => percentShares(seasonalData.map((e) => e.value)), [seasonalData]);

  // Do bieżącego roku + 1, jak strzałki: w grudniu dopiero rok następny daje trwającą zimę.
  const kpiYears = useMemo(() => {
    const biezacy = new Date().getFullYear();
    const wybrany = Number(kpiYear) || biezacy;
    const lata = [];
    for (let y = Math.min(2025, wybrany); y <= Math.max(biezacy + 1, wybrany); y += 1) lata.push(y);
    return lata;
  }, [kpiYear]);

  const barName = metric === 'value' ? t('statistics.bar.value') : t('statistics.bar.count');
  const tooltipFormatter = (value) => (metric === 'value' ? currencyFmt.format(Number(value || 0)) : numberFmt.format(Number(value || 0)));
  const yTickFormatter = (val) => (metric === 'value' ? currencyFmt.format(Number(val || 0)) : val);
  const opisMiary = metric === 'value' ? t('statistics.chart.revenuePln') : t('statistics.chart.reservationsCount');

  return (
    <LocalContainer>
      <Header>
        <Title>{t('statistics.header')}</Title>
        <Subtitle>{t('statistics.subtitle')}</Subtitle>
      </Header>

      {/* ── Compact controls bar ─────────────────────────────────────────── */}
      <ControlsBar data-tour="stats-controls">
        <MetricPills>
          <PillButton type="button" $active={metric === 'count'} onClick={() => setMetric('count')}>
            {t('statistics.metric.count')}
          </PillButton>
          <PillButton type="button" $active={metric === 'value'} onClick={() => setMetric('value')}>
            {t('statistics.metric.value')}
          </PillButton>
        </MetricPills>
        <ControlsDivider />
        <YearNav>
          <ArrowButton type="button" data-cy="stats-prev-year" onClick={() => zmienRok(-1)} disabled={chartYear <= 2000} aria-label={t('statistics.prevYear')}>◀</ArrowButton>
          <YearText data-cy="stats-chart-year">{chartYear}</YearText>
          <ArrowButton type="button" data-cy="stats-next-year" onClick={() => zmienRok(1)} disabled={chartYear >= new Date().getFullYear() + 1} aria-label={t('statistics.nextYear')}>▶</ArrowButton>
        </YearNav>
        <ControlsHint>
          {opisMiary} · <strong>{chartYear}</strong>
          {/* Bez przezroczystości: z nią podpowiedź miała 2,9:1 zamiast wymaganych 4,5:1. */}
          <span style={{ marginLeft: 8, fontSize: '0.76rem' }}>· {t('statistics.chart.hint')}</span>
        </ControlsHint>
      </ControlsBar>

      <MainChartCard>
        <ChartRowHeader>
          <ChartTitle style={{ margin: 0 }}>{t('statistics.chart.monthlyActivity')}</ChartTitle>
          <ChartSubtitle style={{ margin: 0 }}>{opisMiary} · {chartYear}</ChartSubtitle>
        </ChartRowHeader>
        {!wykresGotowy ? <WykresWczytywany wysokosc={210} tekst={t('statistics.loading')} /> : (
          <ResponsiveContainer width="100%" height={210}>
            <ComposedChart data={monthlyData} margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--primary)" stopOpacity={1} />
                  <stop offset="100%" stopColor="var(--info)" stopOpacity={0.72} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              <XAxis dataKey="month" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 11 }} tickFormatter={yTickFormatter} axisLine={false} tickLine={false} width={metric === 'value' ? 80 : 36} />
              <Tooltip
                separator={SEPARATOR_DYMKA}
                formatter={(val, name) => (name === 'Trend' ? null : [tooltipFormatter(val), barName])}
                wrapperStyle={{ outline: 'none' }}
                contentStyle={{ background: 'var(--panel-bg)', border: '1px solid var(--border)', borderRadius: 10, color: 'var(--text)', fontSize: '0.88rem' }}
                labelStyle={{ color: 'var(--text)', fontWeight: 700, marginBottom: 4 }}
                cursor={{ fill: 'color-mix(in srgb, var(--primary) 7%, transparent)' }}
              />
              <Bar
                dataKey={pole}
                fill="url(#barGrad)"
                name={barName}
                radius={[5, 5, 0, 0]}
                maxBarSize={38}
                onClick={(data) => { if (data?.monthIndex) applyKpiMonthFilter(chartYear, data.monthIndex); }}
                style={{ cursor: 'pointer' }}
              />
              {/* Złoty akcent linii trendu, celowo poza tokenami tonu (nie jest ostrzeżeniem),
                  ten sam odcień na całej stronie dla serii „Trend”. */}
              <Line type="monotone" dataKey={pole} stroke="#f2c94c" strokeWidth={2} dot={false}
                activeDot={{ r: 4, fill: '#f2c94c', stroke: 'var(--card-bg)', strokeWidth: 2 }} name="Trend" legendType="none" />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </MainChartCard>

      <SmallChartsGrid>
        <ChartCard>
          <ChartTitle>{t('statistics.chart.equipmentStatus')}</ChartTitle>
          {/* Bez odpowiedzi serwera nie ma liczby sztuk, a „Łącznie: 0” obok komunikatu o błędzie
              wyglądało na prawdziwe dane. */}
          <ChartSubtitle>{sprzetDzis ? t('statistics.chart.equipmentTotal', { count: numberFmt.format(liczbaSztukDzis) }) : '\u00a0'}</ChartSubtitle>
          <ResponsiveContainer width="100%" height={WYSOKOSC_PACZKA}>
            <PieChart>
              <defs>
                {equipmentDonutData.map((e, i) => (
                  <radialGradient key={i} id={`dg${i}`} cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor={e.color} stopOpacity={0.9} />
                    <stop offset="100%" stopColor={e.color} stopOpacity={0.65} />
                  </radialGradient>
                ))}
              </defs>
              <Pie data={equipmentDonutData} cx="50%" cy="50%" innerRadius={52} outerRadius={76}
                paddingAngle={equipmentDonutData.length > 1 ? 4 : 0} dataKey="value" startAngle={90} endAngle={-270}>
                {equipmentDonutData.map((entry, i) => <Cell key={i} fill={`url(#dg${i})`} stroke="none" />)}
              </Pie>
              <Tooltip
                separator={SEPARATOR_DYMKA}
                formatter={(val, name) => [`${numberFmt.format(val)} ${t('statistics.chart.itemsUnit')}`, name]}
                contentStyle={{ background: 'var(--panel-bg)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: '0.82rem' }}
              />
            </PieChart>
          </ResponsiveContainer>
          {liczbaSztukDzis > 0 && (
            <DonutLegend data-cy="equipment-status-legend">
              {equipmentDonutData.map((entry, i) => (
                <DonutLegendRow key={entry.name}>
                  <DonutLegendDot $color={entry.color} aria-hidden="true" />
                  <span>{entry.name}</span>
                  <span>{numberFmt.format(entry.value)} {t('statistics.chart.itemsUnit')}</span>
                  <strong>{equipmentShares[i]}%</strong>
                </DonutLegendRow>
              ))}
            </DonutLegend>
          )}
        </ChartCard>

        <ChartCard>
          <ChartTitle>{t('statistics.chart.seasonality')}</ChartTitle>
          <ChartSubtitle>{t('statistics.chart.bySeason', { year: chartYear })}</ChartSubtitle>
          {!wykresGotowy ? <WykresWczytywany wysokosc={WYSOKOSC_PACZKA} tekst={t('statistics.loading')} /> : seasonalData.length > 0 ? (
            <>
            <ResponsiveContainer width="100%" height={WYSOKOSC_PACZKA}>
              <PieChart>
                <defs>
                  {seasonalData.map((s, i) => (
                    <linearGradient key={i} id={`sg${i}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={s.color} stopOpacity={0.95} />
                      <stop offset="100%" stopColor={s.color} stopOpacity={0.6} />
                    </linearGradient>
                  ))}
                </defs>
                {/* Bez etykiet na obwodzie: nawet sam procent wychodził w wąskiej karcie poza SVG.
                    Nazwa, wartość i procent są w tabelce pod pierścieniem. */}
                <Pie data={seasonalData} cx="50%" cy="50%" innerRadius={52} outerRadius={76} dataKey="value" minAngle={6}
                  paddingAngle={seasonalData.length > 1 ? 4 : 0}
                  onClick={(entry) => { if (entry?.key) applyKpiSeasonFilter(chartYear, entry.key); }}>
                  {seasonalData.map((entry, i) => (
                    <Cell key={i} fill={`url(#sg${i})`} stroke={sezonWykresuWybrany(entry.key) ? 'var(--text)' : 'none'}
                      strokeWidth={sezonWykresuWybrany(entry.key) ? 2 : 0} style={{ cursor: 'pointer' }}
                      onClick={() => applyKpiSeasonFilter(chartYear, entry.key)} />
                  ))}
                </Pie>
                <Tooltip
                  separator={SEPARATOR_DYMKA}
                  formatter={(val, name, item) => [
                    metric === 'value' ? currencyFmt.format(val) : `${numberFmt.format(val)} ${t('statistics.chart.reservationsUnit')}`,
                    `${name} · ${decimalFmt.format(Number(item?.payload?.percent || 0))}%`,
                  ]}
                  contentStyle={{ background: 'var(--panel-bg)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: '0.82rem' }}
                />
              </PieChart>
            </ResponsiveContainer>
            {/* Wiersze są przyciskami: klik filtruje statystyki po porze roku jak klik w wycinek,
                drugi klik zdejmuje filtr. Wciśnięty tylko dla roku z wykresu (sezonWykresuWybrany). */}
            <DonutLegend data-cy="seasonality-legend">
              {seasonalData.map((entry, i) => {
                const active = sezonWykresuWybrany(entry.key);
                return (
                  <li key={entry.key}>
                    <DonutLegendButton
                      type="button"
                      $active={active}
                      aria-pressed={active}
                      onClick={() => applyKpiSeasonFilter(chartYear, active ? '' : entry.key)}
                    >
                      <DonutLegendDot $color={entry.color} aria-hidden="true" />
                      <span>{entry.name}</span>
                      <span>{metric === 'value' ? currencyFmt.format(entry.value) : `${numberFmt.format(entry.value)} ${t('statistics.chart.reservationsUnit')}`}</span>
                      <strong>{seasonalShares[i]}%</strong>
                    </DonutLegendButton>
                  </li>
                );
              })}
            </DonutLegend>
            </>
          ) : (
            <div style={{ height: 185, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
              {t('statistics.chart.noDataFor', { year: chartYear })}
            </div>
          )}
        </ChartCard>

        <ChartCard>
          <ChartTitle>{t('statistics.chart.trend')}</ChartTitle>
          <ChartSubtitle>{opisMiary} · {chartYear}</ChartSubtitle>
          {!wykresGotowy ? <WykresWczytywany wysokosc={185} tekst={t('statistics.loading')} /> : (
            <ResponsiveContainer width="100%" height={185}>
              <AreaChart data={trendData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}
                onClick={(state) => { const idx = trendData.findIndex((item) => item.month === state?.activeLabel); if (idx >= 0) applyKpiMonthFilter(chartYear, trendData[idx].monthIndex); }}>
                <defs>
                  <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--primary)" stopOpacity={0.32} />
                    <stop offset="95%" stopColor="var(--primary)" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: 'var(--text-muted)', fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 10 }} tickFormatter={yTickFormatter} axisLine={false} tickLine={false} width={metric === 'value' ? 72 : 30} />
                <Tooltip formatter={tooltipFormatter} separator={SEPARATOR_DYMKA}
                  contentStyle={{ background: 'var(--panel-bg)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: '0.82rem' }}
                  labelStyle={{ color: 'var(--text-muted)', fontWeight: 600 }} />
                <Area type="monotone" dataKey={metric === 'value' ? 'value' : 'count'} name={barName} stroke="var(--primary)" strokeWidth={2}
                  fill="url(#areaGrad)" dot={{ r: 2, strokeWidth: 0, fill: 'var(--primary)' }}
                  activeDot={{ r: 4, fill: 'var(--card-bg)', stroke: 'var(--primary)', strokeWidth: 2 }} style={{ cursor: 'pointer' }} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </SmallChartsGrid>

      <KPIContainer>
        <KPIHeader>
          <div>
            <SectionTitle>{t('statistics.kpi.title')}</SectionTitle>
            <PeriodLabel>{t('statistics.periodFor')}: {zakresOkresu}</PeriodLabel>
          </div>
          {/* Krok przewodnika podświetla same filtry (o nich mówi), nie cały nagłówek sekcji:
              na telefonie nagłówek z filtrami był wyższy niż miejsce nad kartą przewodnika. */}
          <KPIFiltersRow data-tour="stats-kpi">
            <FilterDropdown
              waski
              value={kpiYear}
              onChange={(v) => setKpiYear(v || String(new Date().getFullYear()))}
              options={kpiYears.map((y) => ({ value: String(y), label: String(y) }))}
              placeholder={t('statistics.year')}
              label={t('statistics.year')}
              dataCy="stats-year"
            />
            {/* Przy sezonie żadna pozycja nie jest wybrana i pole pokazuje „Miesiąc”. */}
            <FilterDropdown
              value={kpiSeason ? null : kpiMonth}
              onChange={(v) => { setKpiMonth(v); setKpiSeason(''); }}
              options={[
                { value: '', label: t('statistics.wholeYear') },
                ...localizedMonths.map((m, idx) => ({ value: String(idx + 1), label: m })),
              ]}
              placeholder={t('statistics.month')}
              label={t('statistics.month')}
              dataCy="stats-month"
            />
            <FilterDropdown
              value={kpiSeason}
              onChange={(v) => { setKpiSeason(v); if (v) setKpiMonth(''); }}
              options={[
                { value: '', label: t('statistics.noSeason') },
                ...SEZONY.map((s) => ({ value: s.value, label: `${nazwaSezonu(s.value)} ${rokSezonu(s.value, okres.rok)}` })),
              ]}
              placeholder={t('statistics.noSeason')}
              label={t('statistics.seasonLabel')}
              dataCy="stats-season"
            />
            <SecondaryButton type="button" onClick={clearKpiFilters} title={t('statistics.clearFilters')}>
              {t('common.clear')}
            </SecondaryButton>
          </KPIFiltersRow>
        </KPIHeader>

        <KPIBadgesRow>
          <KPIBadge $tone="primary" data-cy="stats-period">{t('statistics.badge.period', { label: etykietaOkresu })}</KPIBadge>
          {/* Liczby dopiero po odpowiedzi: w trakcie wczytywania stały tu dane poprzedniego okresu,
              a po błędzie zera obok komunikatu. */}
          {!isLoading && kpi && (
            <>
              <KPIBadge data-cy="stats-badge-active">{t('statistics.badge.activeCustomers', { count: numberFmt.format(aktywni) })}</KPIBadge>
              <KPIBadge>{t('statistics.badge.availableToday', { count: numberFmt.format(Number(sprzet.availableEquipment || 0)) })}</KPIBadge>
            </>
          )}
        </KPIBadgesRow>

        {loadError && !isLoading && <div role="alert" style={{ color: 'var(--danger)', margin: '8px 0 16px' }}>{t('statistics.loadError')}</div>}

        {!isLoading && kpi && (
          <SummaryGrid>
            <SummaryCard $accent="var(--success)">
              <SummaryLabel>{t('statistics.summary.rentalValue')}</SummaryLabel>
              <SummaryValue>{currencyFmt.format(przychodOkresu)}</SummaryValue>
              <SummaryMeta data-cy="stats-change-revenue">{formatZmiany(porownanie.przychod)}</SummaryMeta>
              {/* Ta liczba NIE jest sprzedażą z faktur, a klient pyta o to przy pierwszym
                  spojrzeniu na oba ekrany. Kafelek „Sprzedaż brutto” na liście faktur liczy
                  wystawione dokumenty wg daty wystawienia, ten liczy rezerwacje po rabatach. */}
              <SummaryHint>{t('statistics.summary.rentalValueHint')}</SummaryHint>
            </SummaryCard>
            <SummaryCard $accent="var(--primary)">
              <SummaryLabel>{t('statistics.card.reservationsPeriod')}</SummaryLabel>
              <SummaryValue>{numberFmt.format(rezerwacjeOkresu)}</SummaryValue>
              <SummaryMeta data-cy="stats-change-rentals">{formatZmiany(porownanie.rezerwacje)}</SummaryMeta>
            </SummaryCard>
            <SummaryCard $accent="var(--warning)">
              <SummaryLabel>{t('statistics.card.utilization')}</SummaryLabel>
              <SummaryValue>{decimalFmt.format(Number(p.utilizationPct || 0))}%</SummaryValue>
              <SummaryMeta>{t('statistics.card.utilizationMeta', { rented: numberFmt.format(Number(p.rentedDays || 0)), available: numberFmt.format(dniDoDyspozycji) })}</SummaryMeta>
            </SummaryCard>
            {/* Czwarty, wyłącznie rozróżniający akcent, bez własnego tokenu tonu */}
            <SummaryCard $accent="#7c3aed">
              <SummaryLabel>{t('statistics.finances.valuePerActiveCustomer')}</SummaryLabel>
              <SummaryValue data-cy="stats-value-per-customer">{currencyFmt.format(klienci.wartoscNaKlienta)}</SummaryValue>
              <SummaryMeta>{t('statistics.card.activeOfAll', { active: numberFmt.format(aktywni), total: numberFmt.format(klienci.wszyscy) })}</SummaryMeta>
            </SummaryCard>
          </SummaryGrid>
        )}

        <KPIGrid>
          {isLoading && <div>{t('statistics.loading')}</div>}
          {!isLoading && kpi && (
            <>
              <Section style={{ borderLeft: '4px solid var(--primary)' }}>
                <SectionHeader>{t('statistics.section.demand')}</SectionHeader>
                <SectionGrid>
                  <KPICard><p>{t('statistics.card.reservationsPeriod')}</p><span>{numberFmt.format(rezerwacjeOkresu)}</span></KPICard>
                  <KPICard><p>{t('statistics.card.reservationsYear', { year: okres.rok })}</p><span>{numberFmt.format(Number(kpi.rentals?.rentalsThisYear || 0))}</span></KPICard>
                  <KPICard><p>{t('statistics.card.avgDays')}</p><span>{t('statistics.card.daysValue', { value: decimalFmt.format(Number(p.avgRentalDays || 0)) })}</span></KPICard>
                  <KPICard data-cy="stats-change-card">
                    <p>{t('statistics.card.change')}</p>
                    <span>{porownanie.rezerwacje == null ? '-' : procentZmiany(porownanie.rezerwacje)}</span>
                    <small>{formatZmiany(porownanie.rezerwacje, false)}</small>
                  </KPICard>
                </SectionGrid>
              </Section>

              <Section style={{ borderLeft: '4px solid var(--success)' }}>
                <SectionHeader>{t('statistics.section.finances')}</SectionHeader>
                <SectionGrid>
                  <KPICard><p>{t('statistics.summary.rentalValue')}</p><span>{currencyFmt.format(przychodOkresu)}</span></KPICard>
                  <KPICard><p>{t('statistics.card.revenueYear', { year: okres.rok })}</p><span>{currencyFmt.format(Number(kpi.finances?.revenueThisYear || 0))}</span></KPICard>
                  <KPICard><p>{t('statistics.card.avgReservationValuePeriod')}</p><span>{currency2Fmt.format(Number(p.avgRevenuePerRental || 0))}</span></KPICard>
                  {/* Ten sam zapis co w kafelku nad sekcją (pełne złote): ta sama liczba była tam
                      „560 zł”, a tu „560,00 zł”. */}
                  <KPICard><p>{t('statistics.finances.valuePerActiveCustomer')}</p><span>{currencyFmt.format(klienci.wartoscNaKlienta)}</span></KPICard>
                </SectionGrid>
              </Section>

              <Section style={{ borderLeft: '4px solid var(--warning)' }}>
                <SectionHeader>{t('statistics.section.operations')}</SectionHeader>
                <SectionGrid>
                  <KPICard><p>{t('statistics.card.utilization')}</p><span>{decimalFmt.format(Number(p.utilizationPct || 0))}%</span></KPICard>
                  <KPICard>
                    <p>{t('statistics.card.rentedOnce')}</p>
                    <span>{t('statistics.card.rentedOnceValue', { count: numberFmt.format(Number(p.equipmentRented || 0)), percent: decimalFmt.format(Number(sprzet.usabilityPercent || 0)) })}</span>
                  </KPICard>
                  <KPICard><p>{t('statistics.card.inUseToday')}</p><span>{numberFmt.format(Number(sprzet.inUseEquipment || 0))}</span></KPICard>
                  <KPICard><p>{t('statistics.card.availableToday')}</p><span>{numberFmt.format(Number(sprzet.availableEquipment || 0))}</span></KPICard>
                </SectionGrid>
              </Section>

              {/* Klienci: czwarty, wyłącznie rozróżniający akcent sekcji, bez własnego tokenu tonu */}
              <Section style={{ borderLeft: '4px solid #9b59b6' }}>
                <SectionHeader>{t('statistics.section.customers')}</SectionHeader>
                <SectionGrid>
                  <KPICard><p>{t('statistics.card.activeCustomers')}</p><span>{numberFmt.format(aktywni)}</span></KPICard>
                  <KPICard><p>{t('statistics.card.activeShare')}</p><span data-cy="stats-active-share">{decimalFmt.format(klienci.udzialAktywnych)}%</span></KPICard>
                  <KPICard><p>{t('statistics.card.newCustomers')}</p><span>{numberFmt.format(Number(p.customersNew || 0))}</span></KPICard>
                  <KPICard><p>{t('statistics.card.reservationsPerCustomer')}</p><span data-cy="stats-rentals-per-customer">{decimal2Fmt.format(klienci.rezerwacjeNaKlienta)}</span></KPICard>
                </SectionGrid>
              </Section>
            </>
          )}
        </KPIGrid>
      </KPIContainer>

      {/* W trakcie wczytywania sekcja zostaje z wysokością sprzed zmiany, ale bez liczb
          poprzedniego okresu (pierwsze wczytanie nie ma czego trzymać, więc sekcji jeszcze nie ma). */}
      {(advancedMetrics || (!zaawansowaneGotowe && wysokoscZaawansowanychRef.current > 0)) && (
        <KPIContainer
          ref={sekcjaZaawansowanaRef}
          data-cy="stats-advanced"
          aria-busy={advancedMetrics ? undefined : 'true'}
          style={{ marginTop: 32, minHeight: advancedMetrics ? undefined : wysokoscZaawansowanychRef.current }}
        >
          <KPIHeader>
            <div>
              <SectionTitle>{t('statistics.advanced.title')}</SectionTitle>
              <PeriodLabel>{t('statistics.badge.period', { label: etykietaOkresu })}</PeriodLabel>
            </div>
          </KPIHeader>

          {!advancedMetrics ? <div>{t('statistics.loading')}</div> : (
            <>
              <SectionTitle style={{ fontSize: '1rem', marginBottom: 10 }}>{t('statistics.card.utilization')}</SectionTitle>
              <SummaryGrid>
                <SummaryCard $accent="#7c3aed">
                  <SummaryLabel>{t('statistics.card.utilization')}</SummaryLabel>
                  <SummaryValue>{decimalFmt.format(advancedMetrics.utilization.utilizationPct)}%</SummaryValue>
                  <SummaryMeta>{t('statistics.card.utilizationMeta', {
                    rented: numberFmt.format(advancedMetrics.utilization.rentedDays),
                    available: numberFmt.format(advancedMetrics.utilization.equipmentCount * advancedMetrics.utilization.periodDays),
                  })}</SummaryMeta>
                </SummaryCard>
                <SummaryCard $accent="#0891b2">
                  <SummaryLabel>{t('statistics.advanced.equipmentCount')}</SummaryLabel>
                  <SummaryValue>{numberFmt.format(advancedMetrics.utilization.equipmentCount)}</SummaryValue>
                  <SummaryMeta>{t('statistics.advanced.periodDays')}: {advancedMetrics.utilization.periodDays}</SummaryMeta>
                </SummaryCard>
              </SummaryGrid>

              {advancedMetrics.utilization.byCategory.length > 0 && (
                <>
                  <SectionTitle style={{ fontSize: '0.9rem', margin: '16px 0 8px' }}>{t('statistics.advanced.byCategory')}</SectionTitle>
                  <ResponsiveContainer width="100%" height={Math.max(120, advancedMetrics.utilization.byCategory.length * 36)}>
                    <BarChart data={advancedMetrics.utilization.byCategory} layout="vertical" margin={{ top: 0, right: 24, left: 0, bottom: 0 }}>
                      <XAxis type="number" domain={[0, 100]} unit="%" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                      <YAxis type="category" dataKey="category" width={100} tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                      <Tooltip formatter={(v) => `${decimalFmt.format(v)}%`} separator={SEPARATOR_DYMKA}
                        contentStyle={{ background: 'var(--panel-bg)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: '0.82rem' }} />
                      <Bar dataKey="utilizationPct" name={t('statistics.card.utilization')} fill="#7c3aed" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </>
              )}

              {advancedMetrics.revenuePerItem.length > 0 && (
                <>
                  <SectionTitle style={{ fontSize: '1rem', margin: '24px 0 10px' }}>{t('statistics.advanced.revenuePerItem')}</SectionTitle>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid var(--input-border)' }}>
                          <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-muted)' }}>{t('statistics.advanced.itemName')}</th>
                          <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-muted)' }}>{t('statistics.advanced.category')}</th>
                          <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--text-muted)' }}>{t('statistics.advanced.itemRevenue')}</th>
                          <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--text-muted)' }}>{t('statistics.advanced.itemRentals')}</th>
                          <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--text-muted)' }}>{t('statistics.advanced.itemAvg')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {advancedMetrics.revenuePerItem.slice(0, 20).map((item, idx) => (
                          <tr key={item.equipmentId || idx} style={{ borderBottom: '1px solid var(--border)' }}>
                            {/* Numer przy nazwie: sztuki tego samego modelu mają tę samą nazwę. */}
                            <td style={{ padding: '5px 8px', color: 'var(--text)' }}>
                              {item.name}
                              {item.inventoryNumber && ' '}
                              {item.inventoryNumber && (
                                <span data-cy="stats-item-number" style={{ marginLeft: 2, color: 'var(--text-muted)', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                                  {t('statistics.advanced.inventoryNumber', { number: item.inventoryNumber })}
                                </span>
                              )}
                            </td>
                            <td style={{ padding: '5px 8px', color: 'var(--text-muted)' }}>{item.category}</td>
                            <td style={{ padding: '5px 8px', textAlign: 'right', color: 'var(--text)' }}>{currency2Fmt.format(item.revenue)}</td>
                            <td style={{ padding: '5px 8px', textAlign: 'right', color: 'var(--text-muted)' }}>{numberFmt.format(item.rentalCount)}</td>
                            <td style={{ padding: '5px 8px', textAlign: 'right', color: 'var(--text-muted)' }}>{currency2Fmt.format(item.avgRevenuePerRental)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              <SectionTitle style={{ fontSize: '1rem', margin: '24px 0 10px' }}>{t('statistics.advanced.seasonalityYear', { year: okres.rok })}</SectionTitle>
              <ResponsiveContainer width="100%" height={210}>
                <ComposedChart data={advancedMetrics.seasonality.map((m) => ({ ...m, label: (localizedMonths[m.month - 1] || String(m.month)).slice(0, 3) }))}
                  margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                  <YAxis yAxisId="rev" orientation="left" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} tickFormatter={(v) => currencyFmt.format(v)} width={80} />
                  <YAxis yAxisId="cnt" orientation="right" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                  {/* Formatowanie po kluczu danych, nie po nazwie serii: nazwa jest przetłumaczona,
                      więc porównanie z 'revenue' nigdy nie trafiało i przychód szedł bez „zł”. */}
                  <Tooltip formatter={(v, name, item) => (item?.dataKey === 'revenue' ? currency2Fmt.format(v) : numberFmt.format(v))}
                    separator={SEPARATOR_DYMKA} itemSorter={wKolejnosciSeriiSezonowosci}
                    contentStyle={{ background: 'var(--panel-bg)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', fontSize: '0.82rem' }} />
                  {/* Własna treść legendy: domyślna pisała nazwy kolorem serii (niebieski na białym
                      3,7:1) i dawała ikonom angielskie nazwy dla czytnika ekranu. */}
                  <Legend itemSorter={wKolejnosciSeriiSezonowosci} content={({ payload }) => (
                    <LegendaWykresu dataCy="stats-legend-advanced" pozycje={(payload || []).map((x) => ({ nazwa: x.value, kolor: x.color }))} />
                  )} />
                  <Bar yAxisId="cnt" dataKey="rentals" name={t('statistics.advanced.rentals')} fill="var(--primary)" opacity={0.7} />
                  <Line yAxisId="rev" type="monotone" dataKey="revenue" name={t('statistics.advanced.revenue')} stroke="var(--success)" strokeWidth={2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </>
          )}
        </KPIContainer>
      )}
    </LocalContainer>
  );
}
